import { useState } from "react";

import { Button, Callout, Radio, RadioGroup, Tag } from "@blueprintjs/core";
import { isAfter, isBefore } from "date-fns";

import acceptReview from "@/../../hypetrain/static/img/accept-review.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { CancelBookingRequestConfirmDialog } from "@/components/pieces/coaching/meetings/CancelBookingRequestConfirmDialog";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  BookingRequestTimeslotsQuery,
  useAcceptBookingRequestMutation,
  useCancelBookingRequestMutation,
} from "@/graphql";
import { FullMeeting } from "@/types/meetings";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

interface CoachTimeslotSelectorProps {
  meeting: FullMeeting;
  timeslotData?: BookingRequestTimeslotsQuery;
  loadingTimeslot: boolean;
  cancelBookingRequest: () => void;
  canceling: boolean;
  errorMessage?: string;
  onError: (error?: string) => void;
  isExpired: boolean;
}

function CoachTimeslotSelector({
  meeting,
  timeslotData,
  loadingTimeslot,
  cancelBookingRequest,
  canceling,
  errorMessage,
  onError,
  isExpired,
}: CoachTimeslotSelectorProps) {
  const bookingRequestTimeslots = ensureNonNullArray(
    timeslotData?.bookingRequestTimeslots,
  );
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>();

  const [acceptBookingRequest, { loading: accepting }] =
    useAcceptBookingRequestMutation({
      onCompleted: (data) => {
        if (data?.acceptBookingRequest?.ok) {
          onError();
          window.location.reload();
        } else {
          onError("Something went wrong");
        }
      },
      onError: () => {
        onError("Something went wrong");
      },
    });

  const now = new Date();

  return (
    <Box>
      {!loadingTimeslot &&
        !isExpired &&
        bookingRequestTimeslots.filter(
          (item) =>
            !item.isConflict &&
            item?.timeslot &&
            isAfter(new Date(item?.timeslot), now),
        ).length === 0 && (
          <Box mt={3} mr={3}>
            <Callout icon={null} intent={"warning"}>
              No time slots are available. But you can chat with clients to
              cancel and submit other meeting.
            </Callout>
          </Box>
        )}
      <Box fontWeight={600} mt={3} mb={"10px"}>
        Please select one of the times that work for the customer
      </Box>

      <RadioGroup
        onChange={(e) => setSelectedTimeslot(e.currentTarget.value)}
        selectedValue={selectedTimeslot}
      >
        {bookingRequestTimeslots.map((item) => {
          const { timeslot, preferred, isConflict } = item || {};
          const isOutOfDate = timeslot && isBefore(new Date(timeslot), now);
          return (
            <Radio
              key={timeslot}
              disabled={isConflict || isOutOfDate || isExpired}
              labelElement={
                <Box display={"inline-flex"}>
                  {formatDate(timeslot, "eeee, MMM do 'at' h:mm a z")}
                  {preferred && (
                    <Tag
                      round
                      minimal
                      color="#FFF5DC"
                      style={{ marginLeft: 4, fontSize: 12, fontWeight: 600 }}
                      icon="star"
                      intent="warning"
                    >
                      CLIENT PREFERRED
                    </Tag>
                  )}
                  {isConflict && (
                    <Tag
                      round
                      minimal
                      color="#FFE9E9"
                      style={{ marginLeft: 4, fontSize: 12, fontWeight: 600 }}
                      icon="calendar"
                      intent="danger"
                    >
                      CONFLICT
                    </Tag>
                  )}
                </Box>
              }
              value={timeslot ?? ""}
            />
          );
        })}
      </RadioGroup>

      {errorMessage && (
        <Box mt={3}>
          <Callout icon={null} intent={"danger"}>
            {errorMessage}
          </Callout>
        </Box>
      )}

      <Flex flexWrap="wrap" mt={3}>
        <Box>
          <Button
            intent="primary"
            text="Accept"
            large
            loading={accepting}
            disabled={!selectedTimeslot || accepting || isExpired}
            onClick={() => {
              if (!selectedTimeslot) return;
              acceptBookingRequest({
                variables: {
                  meetingId: meeting?.id,
                  timeslot: selectedTimeslot,
                },
              });
            }}
          />
        </Box>

        <Box ml={2}>
          <Button
            text="Decline"
            large
            loading={canceling}
            disabled={canceling}
            onClick={() => {
              cancelBookingRequest();
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
}

interface BookingRequestPendingProps {
  isCoach: boolean;
  meeting: FullMeeting;
  timeslotData?: BookingRequestTimeslotsQuery;
  loadingTimeslot: boolean;
}

export function BookingRequestPending({
  isCoach,
  meeting,
  timeslotData,
  loadingTimeslot,
}: BookingRequestPendingProps) {
  const title = isCoach
    ? "Accept this service request?"
    : "Your request has been sent";

  const { expiredAt } = meeting;
  const description = isCoach
    ? `Please respond by ${formatDate(expiredAt, "eeee, MMM do 'at' h:mm a z")}`
    : "Waiting for coach to accept";
  const [errorMessage, setErrorMessage] = useState<string>();
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const onError = () => setErrorMessage("Something went wrong");

  const [cancelBookingRequest, { loading: canceling }] =
    useCancelBookingRequestMutation({
      onCompleted: (data) => {
        if (data?.cancelBookingRequest?.ok) {
          setErrorMessage(undefined);
          window.location.reload();
        } else {
          onError();
        }
      },
      onError,
    });

  const now = new Date();
  const isExpired = Boolean(expiredAt && isAfter(now, new Date(expiredAt)));

  return (
    <Box mb={24} p={24} backgroundColor="#FFF" className={CARD_CLASSES}>
      <Flex alignItems={isCoach ? "flex-start" : "center"}>
        <EmbedSvg src={acceptReview} width={64} />
        <Flex
          ml={3}
          flex={1}
          flexDirection={["column", "column", "column", "row"]}
          flexWrap={"wrap"}
        >
          <Box flex={1} minWidth={250} mr={2}>
            <Box fontSize={20} fontWeight={600}>
              {title}
            </Box>
            <Box color={SUBTEXT_COLOR} mt={1}>
              {description}
            </Box>
            {isExpired && (
              <Box mt={3} mr={3}>
                <Callout icon={null} intent={"warning"}>
                  Booking request has expired. You can cancel or the automatic
                  system will cancel it.
                </Callout>
              </Box>
            )}
            {isCoach && (
              <CoachTimeslotSelector
                errorMessage={errorMessage}
                onError={setErrorMessage}
                isExpired={isExpired}
                cancelBookingRequest={() => {
                  setConfirmationVisible(true);
                }}
                canceling={canceling}
                loadingTimeslot={loadingTimeslot}
                meeting={meeting}
                timeslotData={timeslotData}
              />
            )}
          </Box>
          {!isCoach && (
            <Box width={["100%", "100%", "100%", "auto"]} minWidth={180} mt={2}>
              <Button
                large={true}
                onClick={() => {
                  setConfirmationVisible(true);
                }}
              >
                Cancel Request
              </Button>
            </Box>
          )}
        </Flex>
        <CancelBookingRequestConfirmDialog
          isCoach={isCoach}
          isOpen={confirmationVisible}
          onCancel={() => setConfirmationVisible(false)}
          onConfirm={() =>
            cancelBookingRequest({
              variables: {
                meetingId: meeting?.id,
              },
            })
          }
          loading={canceling}
        />
      </Flex>
    </Box>
  );
}
